import {
  CreateWcClaimCommand,
  CreateWcClaimCommandInput,
} from "@amzn/ttechclaimintakeservice-client";
import {
  CLIENT_ERROR_RESPONSES,
  HttpStatusCodes,
  ProcessedApiResponse,
  SERVER_ERROR_RESPONSES,
} from "./../types";
import { getAuthenticatedClient } from "../setup/getAuthenticatedClient";
import { MetricsPublisher } from "../../metrics/metrics";
import {
  ANCHOR,
  ASSOCIATE_INFORMATION_CONSTANTS,
  HELMSMAN,
  INCIDENT_TIME_AND_PLACE_CONSTANTS,
  SEDGWICK,
  SEDGWICK_CANADA,
} from "../../forms/constants";
import moment from "moment-timezone";

export async function SendCreateWcClaimCommand(
  input: CreateWcClaimCommandInput
) {
  const { client } = await getAuthenticatedClient();
  if (!client) {
    const responseStruct: ProcessedApiResponse = {
      statusCode: HttpStatusCodes.UNAUTHORIZED,
      data: [],
      warningList: [],
    };
    return responseStruct;
  }
  // Map sedgwickCanada to sedgwick for the API request since the backend
  // only accepts "sedgwick" as a valid TPA value. Keep original for metrics.
  const originalTpa = input.thirdPartyAdjuster;
  if (input.thirdPartyAdjuster === SEDGWICK_CANADA) {
    input.thirdPartyAdjuster = SEDGWICK;
  }
  const command = new CreateWcClaimCommand(input);
  const metricsPublisher = new MetricsPublisher("api.claimIntake");
  const metricsErrorCodePublisher = new MetricsPublisher(
    "claimSubmittedErrorCode"
  );
  const metricsSiteCodePublisher = new MetricsPublisher(
    "claimSubmittedBySiteName"
  );
  const metricsSiteCodePublisherAnchor = new MetricsPublisher(
    "claimSubmittedAnchorSite"
  );
  const metricsSiteCodePublisherSedgwick = new MetricsPublisher(
    "claimSubmittedSedgwickSite"
  );
  const metricsSiteCodePublisherHelmsman = new MetricsPublisher(
    "claimSubmittedHelmsmanSite"
  );
  const metricsSiteCodePublisherSedgwickCanada = new MetricsPublisher(
    "claimSubmittedSedgwickCanadaSite"
  );

  return await client
    .send(command)
    .then((response: any) => {
      const statusCode = response?.$metadata?.httpStatusCode?.toString();
      const processedResponse: ProcessedApiResponse = {
        statusCode,
        warningList: response.warningList,
        data: [
          {
            fieldName: "Associate Employee ID",
            fieldValue: response.associateEmployeeId,
          },
          {
            fieldName: "Created At",
            fieldValue: moment(response.createdAtTimestamp).format(
              "MMMM Do YYYY, h:mm:ss a"
            ),
          },
          {
            fieldName: "Claim Number",
            fieldValue: response.claimNumber,
          },
        ],
      };

      if (
        input[INCIDENT_TIME_AND_PLACE_CONSTANTS.INCIDENT_SITE_NAME] !==
        input[ASSOCIATE_INFORMATION_CONSTANTS.ASSOCIATE_HOME_SITE_CODE]
      ) {
        metricsPublisher.publishIncidentSiteDifferentThanHome();
      }
      //Overall Metrics
      metricsPublisher.publishSubmitClaimLatencyMetrics();
      metricsPublisher.publishClaimSubmittedSuccessMetrics();
      metricsSiteCodePublisher.publishSiteOfSubmittedClaim(
        input.incidentSiteName
      );

      //TPA specific metrics
      if (originalTpa === SEDGWICK) {
        metricsPublisher.publishClaimSubmittedSuccessSedgwickMetrics();
        metricsSiteCodePublisherSedgwick.publishSiteOfSubmittedClaim(
          input.incidentSiteName
        );
      } else if (originalTpa === ANCHOR) {
        metricsPublisher.publishClaimSubmittedSuccessAnchorMetrics();
        metricsSiteCodePublisherAnchor.publishSiteOfSubmittedClaim(
          input.incidentSiteName
        );
      } else if (originalTpa === HELMSMAN) {
        metricsPublisher.publishClaimSubmittedSuccessHelmsmanMetrics();
        metricsSiteCodePublisherHelmsman.publishSiteOfSubmittedClaim(
          input.incidentSiteName
        );
      } else if (originalTpa === SEDGWICK_CANADA) {
        metricsPublisher.publishClaimSubmittedSuccessSedgwickCanadaMetrics();
        metricsSiteCodePublisherSedgwickCanada.publishSiteOfSubmittedClaim(
          input.incidentSiteName
        );
      }

      return processedResponse;
    })
    .catch((error: any) => {
      const statusCode =
        error?.$metadata?.httpStatusCode?.toString() || "Unknown";
      metricsPublisher.publishClaimSubmittedFailure();
      metricsPublisher.publishSubmitClaimLatencyMetrics();
      metricsErrorCodePublisher.publishClaimSubmittedErrorCodeMetrics(
        statusCode
      );
      // Metrics for 4XX error status codes
      if (CLIENT_ERROR_RESPONSES.includes(statusCode)) {
        metricsPublisher.publishClaimSubmittedClientErrorMetrics();
        if (statusCode === HttpStatusCodes.BAD_REQUEST) {
          metricsPublisher.claimValidationError();
        }
      }

      // Metrics for 5XX error status codes
      if (SERVER_ERROR_RESPONSES.includes(statusCode)) {
        metricsPublisher.publishClaimSubmittedServerFailureMetrics();
        if (statusCode === HttpStatusCodes.GATEWAY_TIMEOUT) {
          metricsPublisher.publishClaimSubmittedTimeoutMetrics();
        }
      }

      throw error;
    });
}
